import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import GlobalStyles from "./styles/GlobalStyles";
import Home from "./pages/Home";
import Search from "./pages/Search";
import Leads from "./pages/Leads";
import Settings from "./pages/Settings";
import BusinessProfile from "./pages/BusinessProfile";
import DocumentChat from "./pages/DocumentChat";
import Schedule from "./pages/Schedule";
import Upgrade from "./pages/Upgrade";
import PageNotFound from "./pages/PageNotFound";
import Proposals from "./pages/proposals";
import {
  AppLayoutMain,
  AppLayoutSidebar,
  LoadingAppLayout,
} from "./ui/AppLayout";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";
import ContractChat from "./pages/Contract";
import AuthPage from "./pages/AuthPage";
import {
  SessionAuth,
  useSessionContext,
} from "supertokens-auth-react/recipe/session";
import ProposalGenerator from "./pages/ProposalGenerator";
import OutlineGenerator from "./pages/OutlineGenerator";
import PurchaseSuccess from "./pages/PurchaseSuccess";
import Unsubscribe from "./pages/Unsubscribe";
import PermissionProtected from "./features/auth/PermissionProtected";
import { TrialState } from "./utils/constants";
import Support from "./pages/Support";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SocialAuthCallback from "./features/auth/SocialAuthCallback";
import ProtectedRoute from "./features/auth/ProtectedRoute";
import EmailVerification from "./features/auth/verification-card";
import VerifyEmailPage from "./features/auth/VerifyEmailPage";
import ResetPasswordPage from "./features/auth/ResetPasswordPage";
import { useUiStore } from "./stores/UiStore";
import Pursuit from "./pages/Pursuit";
import { PursuitLayout } from "./features/pursuit/PursuitLayout";
import PursuitTask from "./features/pursuit/task/PursuitTask";
import PursuitTasks from "./features/pursuit/task/PursuitTasks";
import PursuitOverview from "./features/pursuit/PursuitOverview";
import { PursuitChat } from "./features/pursuit/PursuitChat";
import PursuitProposal from "./features/pursuit/PursuitProposal";
import { PursuitMainLayout } from "./features/pursuit/PursuitMainLayout";
import PursuitDocuments from "./features/pursuit/PursuitDocuments";

const RedirectAuthShow = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectToPath = params.get("redirectToPath");
    if (params.get("show") === "signin") {
      navigate("/auth/signin", { replace: true });
    }
    if (redirectToPath === "") {
      navigate("/auth/signin", { replace: true });
    }
  }, [location, navigate]);

  return null;
};

function AuthRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectToPath = params.get("redirectToPath") || "/";
  useUiStore.setState({ redirectUrl: redirectToPath });

  return (
    <Navigate
      to={`/auth/signin?redirectToPath=${encodeURIComponent(redirectToPath)}`}
      replace
    />
  );
}

function App() {
  const session = useSessionContext();
  const payload = session.accessTokenPayload;
  const allowAccess =
    !session.loading &&
    session.doesSessionExist &&
    (payload.paidPlan || payload.isTrial === TrialState.TRIAL_ONGOING);
  return (
    <>
      <ReactQueryDevtools initialIsOpen={false} />
      <GlobalStyles />
      <Routes>
        <Route path="/auth" element={<AuthRedirect />} />
        <Route path="/auth/signin" element={<AuthPage isSignIn={true} />} />
        <Route path="/auth/signup" element={<AuthPage isSignIn={false} />} />
        <Route path="/auth/callback/google" element={<SocialAuthCallback />} />
        <Route
          path="/auth/callback/linkedin"
          element={<SocialAuthCallback />}
        />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth" element={<RedirectAuthShow />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route
          element={
            <SessionAuth>
              <ProtectedRoute>
                {session.loading ? <LoadingAppLayout /> : <Outlet />}
              </ProtectedRoute>
            </SessionAuth>
          }
        >
          <Route path="thankyou" element={<PurchaseSuccess />} />

          <Route element={<AppLayoutSidebar />}>
            {!allowAccess ? (
              <Route element={<AppLayoutMain />}>
                <Route path="*" element={<Upgrade />} />
              </Route>
            ) : (
              <>
                {/* Pursuit has a different main panel layout than the rest of the application */}
                <Route path="journey-hub" element={<PursuitMainLayout />}>
                  <Route index element={<Pursuit />} />
                  <Route path=":pursuit_id" element={<PursuitLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />

                    {/* Tab routes */}
                    <Route path="overview" element={<PursuitOverview />} />
                    <Route path="chat" element={<PursuitChat />} />
                    <Route path="proposal" element={<PursuitProposal />} />
                    <Route path="documents" element={<PursuitDocuments />} />
                    <Route path="tasks">
                      <Route index element={<PursuitTasks />} />
                      <Route path=":task_id" element={<PursuitTask />} />
                    </Route>
                  </Route>
                </Route>

                <Route element={<AppLayoutMain />}>
                  <Route index element={<Navigate replace to="/home" />} />
                  <Route path="home" element={<Home />} />
                  <Route path="search" element={<Search />} />
                  <Route path="leads" element={<Leads />} />
                  <Route
                    path="proposals"
                    element={
                      <PermissionProtected permission="proposal">
                        <Proposals />
                      </PermissionProtected>
                    }
                  />

                  <Route path="settings" element={<Settings />} />
                  <Route path="support" element={<Support />} />

                  <Route path="contract/:searchType/:proposal_id">
                    {/* Main contract chat */}
                    <Route index element={<ContractChat />} />

                    {/* Generate routes */}
                    <Route
                      path="generate"
                      element={
                        <PermissionProtected permission="proposal">
                          <OutlineGenerator />
                        </PermissionProtected>
                      }
                    />
                    <Route
                      path="generate/editor"
                      element={
                        <PermissionProtected permission="proposal">
                          <ProposalGenerator />
                        </PermissionProtected>
                      }
                    />
                  </Route>
                  <Route
                    path="contract/:proposal_id/generate/editor"
                    element={
                      <PermissionProtected permission="proposal">
                        <ProposalGenerator />
                      </PermissionProtected>
                    }
                  />
                  <Route
                    path=":searchType/:proposal_id/editor"
                    element={
                      <PermissionProtected permission="proposal">
                        <ProposalGenerator />
                      </PermissionProtected>
                    }
                  />
                  <Route
                    path="business_profile"
                    element={<BusinessProfile />}
                  />
                  <Route path="chat" element={<DocumentChat />} />
                  <Route path="schedule" element={<Schedule />} />

                  <Route path="upgrade" element={<Upgrade />} />
                </Route>
              </>
            )}
          </Route>
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      <Toaster
        position="top-center"
        gutter={12}
        containerStyle={{ margin: "8px" }}
        toastOptions={{
          success: { duration: 3000 },
          error: { duration: 5000 },
          style: {
            fontSize: "16px",
            maxWidth: "500px",
            padding: "8px 12px",
            backgroundColor: "var(--gray-50)",
            color: "var(--gray-700)",
            borderRadius: "8px",
          },
        }}
      />
    </>
  );
}

export default App;
